/**
 * VadRecorder — one-shot mic capture with VAD-gated auto-stop.
 *
 * Records until ~silenceHangFrames of trailing silence (allowed only once
 * minVoicedFrames of voiced audio have accumulated) or maxSec, whichever
 * comes first. Used by the enrollment wizard and the Learn practice screen.
 */
import { floatToPcm16 } from "./mic";
import {
  FRAME_SAMPLES, SAMPLE_RATE, VOICED_THRESHOLD,
  SileroVad, type Vad,
} from "./vad";

export const FRAME_DUR_SEC = FRAME_SAMPLES / SAMPLE_RATE; // 32 ms

export class VadRecorder {
  private ctx: AudioContext | null = null;
  private stream: MediaStream | null = null;
  private node: AudioWorkletNode | null = null;
  private buffers: Float32Array[] = [];
  private vad: Vad | null = null;
  private startMs = 0;
  private stopped = false;
  private resolveRun: ((r: { samples: Int16Array; voicedSec: number }) => void) | null = null;
  private rejectRun: ((e: unknown) => void) | null = null;
  private ticker: ReturnType<typeof setInterval> | null = null;
  private voicedFrames = 0;
  private silenceFrames = 0;
  private voicedDetected = false;

  private firstVoicedBuf = -1;
  private lastVoicedBuf = -1;

  constructor(private readonly opts: {
    maxSec: number;
    silenceHangFrames: number;
    /** Voiced frames required before the silence auto-stop is allowed. */
    minVoicedFrames: number;
    /** Reports accumulated VOICED seconds (not wall-clock elapsed). */
    onTick: (voicedSec: number, voicedDetected: boolean) => void;
    /**
     * When set, the returned samples are trimmed to the voiced span plus
     * this many padding frames on each side. Cuts seconds of dead air off
     * uploads (smaller request, faster server ASR). Leave unset where the
     * silence matters — enrollment uses it for noise-floor / SNR checks.
     */
    trimPadFrames?: number;
  }) {}

  async run(): Promise<{ samples: Int16Array; voicedSec: number }> {
    return new Promise((resolve, reject) => {
      this.resolveRun = resolve;
      this.rejectRun = reject;
      void this.start();
    });
  }

  async stop(): Promise<void> {
    if (this.stopped) return;
    this.stopped = true;
    if (this.ticker) { clearInterval(this.ticker); this.ticker = null; }
    try { this.node?.disconnect(); } catch { /* ignore */ }
    try { await this.ctx?.close(); } catch { /* ignore */ }
    this.stream?.getTracks().forEach((t) => t.stop());
    try { this.vad?.dispose?.(); } catch { /* ignore */ }
    this.ctx = null; this.node = null; this.stream = null; this.vad = null;
  }

  /** Current AudioContext state — lets callers detect a recording that a
   *  backgrounded/slept tab silently suspended, and restart it. */
  ctxState(): AudioContextState | null {
    return this.ctx?.state ?? null;
  }

  /** Manual "I'm done" — finalize immediately with whatever was captured. */
  finishNow(): void {
    this.finalize();
  }

  private async start(): Promise<void> {
    try {
      this.vad = new SileroVad(); await this.vad.init();
      this.vad.reset();

      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      try {
        this.ctx = new AudioContext({ sampleRate: SAMPLE_RATE, latencyHint: "interactive" });
      } catch (err) {
        console.warn("Target 16kHz AudioContext rejected by browser, falling back to native sample rate:", err);
        this.ctx = new AudioContext({ latencyHint: "interactive" });
      }
      await this.ctx.audioWorklet.addModule("/app/mic-worklet.js");
      const src = this.ctx.createMediaStreamSource(this.stream);
      this.node = new AudioWorkletNode(this.ctx, "mic-worklet");
      this.node.port.onmessage = (ev) => { void this.onFrame(ev.data as Float32Array); };
      src.connect(this.node);
      this.startMs = performance.now();
      this.ticker = setInterval(() => {
        const elapsed = (performance.now() - this.startMs) / 1000;
        this.opts.onTick(this.voicedFrames * FRAME_DUR_SEC, this.voicedDetected);
        if (elapsed >= this.opts.maxSec) this.finalize();
      }, 100);
    } catch (e) {
      this.rejectRun?.(e);
      void this.stop();
    }
  }

  private async onFrame(frame: Float32Array): Promise<void> {
    if (this.stopped || frame.length !== FRAME_SAMPLES) return;
    this.buffers.push(new Float32Array(frame));
    let prob = 0;
    try { prob = (await this.vad?.process(frame)) ?? 0; }
    catch { prob = 0; }
    if (this.stopped) return;
    if (prob >= VOICED_THRESHOLD) {
      this.voicedFrames += 1;
      this.silenceFrames = 0;
      if (this.voicedFrames >= 2) this.voicedDetected = true;
      this.lastVoicedBuf = this.buffers.length - 1;
      if (this.firstVoicedBuf < 0) this.firstVoicedBuf = this.buffers.length - 1;
    } else {
      this.silenceFrames += 1;
      if (this.voicedFrames >= this.opts.minVoicedFrames
          && this.silenceFrames >= this.opts.silenceHangFrames) {
        this.finalize();
      }
    }
  }

  private finalize(): void {
    if (this.stopped) return;
    let bufs = this.buffers;
    const pad = this.opts.trimPadFrames;
    if (pad != null && this.firstVoicedBuf >= 0) {
      const start = Math.max(0, this.firstVoicedBuf - pad);
      const end = Math.min(bufs.length, this.lastVoicedBuf + pad + 1);
      bufs = bufs.slice(start, end);
    }
    const total = bufs.reduce((acc, b) => acc + b.length, 0);
    const merged = new Float32Array(total);
    let p = 0;
    for (const b of bufs) { merged.set(b, p); p += b.length; }
    const samples = floatToPcm16(merged);
    const voicedSec = this.voicedFrames * FRAME_DUR_SEC;
    void this.stop().then(() => this.resolveRun?.({ samples, voicedSec }));
  }
}
